import { useState, useEffect, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import { formatPhone } from "../../lib/phoneFormat";
import { useRealtimeTables } from "../../lib/useRealtimeTable";

interface DealContact {
  id: string;
  deal_id: string;
  contact_id: string;
  role: string;
  label: string | null;
  is_primary: boolean;
  receives_texts: boolean;
  receives_emails: boolean;
  phone_override: string | null;
  email_override: string | null;
  notes: string | null;
  created_at: string;
  // Joined from contacts
  contact?: {
    full_name: string | null;
    phone: string | null;
    email: string | null;
  };
}

const ROLE_OPTIONS = [
  { value: "primary", label: "Primary" },
  { value: "spouse", label: "Spouse" },
  { value: "co-owner", label: "Co-Owner" },
  { value: "tenant", label: "Tenant" },
  { value: "property_manager", label: "Property Manager" },
  { value: "other", label: "Other" },
];

export default function DealContacts({ dealId }: { dealId: string }) {
  const [contacts, setContacts] = useState<DealContact[]>([]);
  // The deal is the source of truth for the primary homeowner's name/phone/email
  // (this CRM is deal-centric — `contacts` is a dedup/reference table, not the
  // owner). For the primary row we display these fields, falling back to the
  // joined contact only if the deal's value is missing.
  const [dealPrimary, setDealPrimary] = useState<{
    name: string | null;
    phone: string | null;
    email: string | null;
  } | null>(null);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // New contact form
  const [newName, setNewName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("spouse");
  const [newLabel, setNewLabel] = useState("");
  const [newTexts, setNewTexts] = useState(true);
  const [newEmails, setNewEmails] = useState(true);

  const load = useCallback(async () => {
    const [{ data: dcData }, { data: dealData }] = await Promise.all([
      supabase
        .from("deal_contacts")
        .select(`
          *,
          contact:contacts(full_name, phone, email)
        `)
        .eq("deal_id", dealId)
        .order("is_primary", { ascending: false })
        .order("created_at", { ascending: true }),
      supabase
        .from("deals")
        .select("homeowner_name,homeowner_phone,homeowner_email")
        .eq("id", dealId)
        .single(),
    ]);
    if (dcData) setContacts(dcData as DealContact[]);
    if (dealData) {
      setDealPrimary({
        name: (dealData as { homeowner_name: string | null }).homeowner_name,
        phone: (dealData as { homeowner_phone: string | null }).homeowner_phone,
        email: (dealData as { homeowner_email: string | null }).homeowner_email,
      });
    }
  }, [dealId]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh when the deal itself changes (homeowner rename via Edit Deal,
  // or any other field on this deal) or when deal_contacts are added / removed /
  // toggled. Keeps this widget in sync across tabs and with concurrent edits.
  useRealtimeTables(["deals", "deal_contacts"], load);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    setError(null);

    try {
      // First, find or create the contact
      const nameParts = newName.trim().split(/\s+/);
      const firstName = nameParts[0] || "";
      const lastName = nameParts.slice(1).join(" ") || "";

      // Check if contact already exists by phone
      let contactId: string | null = null;
      if (newPhone.trim()) {
        const phoneLast10 = newPhone.replace(/\D/g, "").slice(-10);
        const { data: existing } = await supabase
          .from("contacts")
          .select("id, phone")
          .limit(100);

        const match = existing?.find((c) => {
          const cPhone = (c.phone || "").replace(/\D/g, "").slice(-10);
          return cPhone === phoneLast10 && phoneLast10.length === 10;
        });
        if (match) contactId = match.id;
      }

      // Create new contact if not found
      if (!contactId) {
        const { data: newContact, error: createErr } = await supabase
          .from("contacts")
          .insert({
            first_name: firstName,
            last_name: lastName,
            phone: newPhone.trim() || null,
            email: newEmail.trim() || null,
          })
          .select("id")
          .single();

        if (createErr) throw createErr;
        contactId = newContact.id;
      }

      // Link contact to deal
      const { error: linkErr } = await supabase
        .from("deal_contacts")
        .insert({
          deal_id: dealId,
          contact_id: contactId,
          role: newRole,
          label: newLabel.trim() || null,
          is_primary: false,
          receives_texts: newTexts,
          receives_emails: newEmails,
        });

      if (linkErr) {
        if (linkErr.message.includes("duplicate")) {
          setError("This contact is already linked to this deal.");
        } else {
          throw linkErr;
        }
      } else {
        // Also update deal's homeowner fields if this changes the name display
        // (keep primary as-is, just add the secondary)
        setAdding(false);
        resetForm();
        await load();
      }
    } catch (err: unknown) {
      const msg = (err as Record<string, unknown>)?.message ?? (err as Record<string, unknown>)?.details ?? String(err);
      setError(typeof msg === "string" ? msg : "Failed to add contact");
    } finally {
      setSaving(false);
    }
  };

  const removeContact = async (dc: DealContact) => {
    if (dc.is_primary) return; // Can't remove primary
    if (!confirm(`Remove ${dc.contact?.full_name || "this contact"} from this deal?`)) return;
    await supabase.from("deal_contacts").delete().eq("id", dc.id);
    await load();
  };

  const toggleTexts = async (dc: DealContact) => {
    await supabase
      .from("deal_contacts")
      .update({ receives_texts: !dc.receives_texts })
      .eq("id", dc.id);
    await load();
  };

  const toggleEmails = async (dc: DealContact) => {
    await supabase
      .from("deal_contacts")
      .update({ receives_emails: !dc.receives_emails })
      .eq("id", dc.id);
    await load();
  };

  function resetForm() {
    setNewName("");
    setNewPhone("");
    setNewEmail("");
    setNewRole("spouse");
    setNewLabel("");
    setNewTexts(true);
    setNewEmails(true);
    setError(null);
  }

  return (
    <div>
      {/* Contact list */}
      <div className="space-y-2">
        {contacts.map((dc) => {
          // Primary row: deal is source of truth (deal-centric CRM).
          // Secondary rows: use the joined contact + per-deal overrides as before.
          const displayName = dc.is_primary
            ? (dealPrimary?.name || dc.contact?.full_name || "Unknown")
            : (dc.contact?.full_name || "Unknown");
          const displayPhone = dc.is_primary
            ? (dealPrimary?.phone || dc.phone_override || dc.contact?.phone || null)
            : (dc.phone_override || dc.contact?.phone || null);
          const displayEmail = dc.is_primary
            ? (dealPrimary?.email || dc.email_override || dc.contact?.email || null)
            : (dc.email_override || dc.contact?.email || null);
          return (
          <div
            key={dc.id}
            className="flex items-center gap-3 bg-slate-50 rounded-lg px-3 py-2"
          >
            {/* Avatar circle */}
            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold text-white shrink-0 ${
              dc.is_primary ? "bg-bardin-teal" : "bg-bardin-orange"
            }`}>
              {displayName[0].toUpperCase()}
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-bardin-ink truncate">
                  {displayName}
                </span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                  dc.is_primary
                    ? "bg-bardin-teal/10 text-bardin-teal"
                    : "bg-bardin-orange/10 text-bardin-orange"
                }`}>
                  {dc.label || dc.role}
                </span>
              </div>
              <div className="text-xs text-gray-400 flex gap-3">
                {displayPhone && (
                  <span>{formatPhone(displayPhone)}</span>
                )}
                {displayEmail && (
                  <span className="truncate">{displayEmail}</span>
                )}
              </div>
            </div>

            {/* Toggles */}
            <div className="flex items-center gap-1.5 shrink-0">
              <button
                onClick={() => toggleTexts(dc)}
                title={dc.receives_texts ? "Receiving texts" : "Not receiving texts"}
                className={`text-xs px-1.5 py-0.5 rounded ${
                  dc.receives_texts ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                }`}
              >
                SMS
              </button>
              <button
                onClick={() => toggleEmails(dc)}
                title={dc.receives_emails ? "Receiving emails" : "Not receiving emails"}
                className={`text-xs px-1.5 py-0.5 rounded ${
                  dc.receives_emails ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-400"
                }`}
              >
                Email
              </button>
              {!dc.is_primary && (
                <button
                  onClick={() => removeContact(dc)}
                  className="text-xs text-red-400 hover:text-red-600 ml-1"
                  title="Remove"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          );
        })}
      </div>

      {/* Add button or form */}
      {!adding ? (
        <button
          onClick={() => setAdding(true)}
          className="mt-3 text-sm text-bardin-teal hover:underline font-medium"
        >
          + Add Contact
        </button>
      ) : (
        <form onSubmit={handleAdd} className="mt-3 bg-slate-50 rounded-lg p-3 space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <input
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Full name"
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
                required
              />
            </div>
            <input
              type="tel"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              placeholder="Phone"
              className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
            />
            <input
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              placeholder="Email"
              className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
            />
            <select
              value={newRole}
              onChange={(e) => setNewRole(e.target.value)}
              className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
            >
              {ROLE_OPTIONS.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="Label (e.g. Husband)"
              className="border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-bardin-teal focus:border-transparent"
            />
          </div>
          <div className="flex items-center gap-4 text-sm">
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={newTexts} onChange={(e) => setNewTexts(e.target.checked)} className="rounded" />
              <span className="text-gray-600">Receives texts</span>
            </label>
            <label className="flex items-center gap-1.5 cursor-pointer">
              <input type="checkbox" checked={newEmails} onChange={(e) => setNewEmails(e.target.checked)} className="rounded" />
              <span className="text-gray-600">Receives emails</span>
            </label>
          </div>
          {error && <p className="text-red-500 text-xs">{error}</p>}
          <div className="flex gap-2">
            <button
              type="submit"
              disabled={saving}
              className="px-4 py-1.5 text-sm font-medium rounded bg-bardin-teal text-white hover:bg-bardin-teal/90 disabled:opacity-50"
            >
              {saving ? "Adding..." : "Add Contact"}
            </button>
            <button
              type="button"
              onClick={() => { setAdding(false); resetForm(); }}
              className="px-4 py-1.5 text-sm font-medium rounded bg-gray-200 text-gray-600 hover:bg-gray-300"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
